import CrossIcon from "@assets/icons/cross.svg";
import Megaphone from "@assets/pictures/megaphone.svg";
import { useI18n } from "@core/i18n/i18n";
import { Announcement, FeaturePreview, isAnnouncement } from "@domain/announcement/announcement";
import { AnnouncementRenderer } from "@ui/pages/announcement-dialog/dialog/announcement-overrides";
import {
	sendAnnouncementCloseEvent,
	sendAnnouncementNextSlideEvent,
	sendAnnouncementPreviousSlideEvent,
} from "@domain/announcement/announcement-analytics";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog } from "@ui/components/dialog/base-dialog";
import { ImageWithLoading } from "@ui/components/common/image-with-loading";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import { isNotNil } from "@utils/assert";
import { AnimatePresence, motion } from "framer-motion";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useState } from "react";

export interface AnnouncementDialogProps extends DialogProps {
	dialogContent: Announcement | FeaturePreview;
	onClose?: (closeModal: () => void) => void;
}

export const AnnouncementDialog: React.FC<AnnouncementDialogProps> = ({ dialogContent, onClose, ...otherProps }) => {
	const [currentPageIndex, setCurrentPageIndex] = useState(0);
	const { format } = useI18n();

	const closeDialog = useCallback(() => {
		isAnnouncement(dialogContent) && sendAnnouncementCloseEvent(dialogContent);
		onClose?.(otherProps.onHide) ?? otherProps.onHide();
	}, [dialogContent, onClose, otherProps]);

	const onNextPressed = useCallback(() => {
		if (currentPageIndex < dialogContent.pages.length - 1) {
			isAnnouncement(dialogContent) && sendAnnouncementNextSlideEvent(dialogContent, currentPageIndex);
			setCurrentPageIndex(currentPageIndex + 1);
		} else {
			closeDialog();
		}
	}, [dialogContent, currentPageIndex, closeDialog]);

	const onBackPressed = useCallback(() => {
		isAnnouncement(dialogContent) && sendAnnouncementPreviousSlideEvent(dialogContent, currentPageIndex);
		setCurrentPageIndex(currentPageIndex - 1);
	}, [dialogContent, currentPageIndex]);

	const { isMobile } = useDimensions();

	if (dialogContent.pages.length === 0) {
		return null;
	}

	return (
		<StyledBaseDialog
			blockScroll
			resizable={false}
			draggable={false}
			dismissableMask={!isAnnouncement(dialogContent)}
			header={
				<Header>
					{!isAnnouncement(dialogContent) && (
						<CrossIconContainer>
							<StyledCrossIcon width={24} height={24} color={colors.grey} onClick={closeDialog} />
						</CrossIconContainer>
					)}
					<Title>{dialogContent.title}</Title>
				</Header>
			}
			footer={
				<>
					{dialogContent.pages.length > 1 && (
						<StepCirclesContainer>
							{dialogContent.pages.map((page, index) => (
								<StepCircle key={page.id} active={currentPageIndex === index} />
							))}
						</StepCirclesContainer>
					)}
					<ButtonsContainer>
						{dialogContent.pages.length > 1 && (
							<AnimatePresence mode="popLayout" presenceAffectsLayout>
								{currentPageIndex > 0 && (
									<motion.div
										layout
										initial={{ opacity: 0, x: -10 }}
										animate={{ opacity: 1, x: 0 }}
										exit={{ opacity: 0, scale: 0, x: -30 }}
									>
										<Button onClick={onBackPressed} uiStyle="secondary">
											{format("global.dialog.back")}
										</Button>
									</motion.div>
								)}
							</AnimatePresence>
						)}
						<Button onClick={onNextPressed}>
							{isMobile
								? dialogContent.pages[currentPageIndex].shortCtaLabel
								: dialogContent.pages[currentPageIndex].ctaLabel}
						</Button>
					</ButtonsContainer>
				</>
			}
			closable={false}
			{...otherProps}
		>
			<PagesContainer>
				<Pages currentPageIndex={currentPageIndex}>
					{dialogContent.pages.map(({ id, title, description, imageUrl, backgroundColor }) => (
						<div key={id}>
							<ImageContainer style={{ backgroundColor }}>
								{isNotNil(imageUrl) ? <AnnouncementImage src={imageUrl} /> : <StyledMegaphone />}
							</ImageContainer>
							<PageTextContent>
								<h3>{title}</h3>
								<div>
									<AnnouncementRenderer content={description} />
								</div>
							</PageTextContent>
						</div>
					))}
				</Pages>
			</PagesContainer>
		</StyledBaseDialog>
	);
};

const Header = styled.div`
	${stack(0, "flexstart", "center")};
`;

const Title = styled.div`
	${row(0, "spacebetween", "center")};
`;

const CrossIconContainer = styled.div`
	${row(0, "flexend", "center")};
	width: 100%;
`;

const StyledCrossIcon = styled(CrossIcon)`
	justify-self: end;
	cursor: pointer;
`;

const PagesContainer = styled.div`
	overflow-x: hidden;
	flex: 1;
`;

const Pages = styled.div<{ currentPageIndex: number }>`
	display: grid;
	grid-auto-flow: column;
	grid-auto-columns: 100%;
	transition: 0.5s transform;
	transform: translateX(${({ currentPageIndex }) => -currentPageIndex * 100}%);
`;

const StepCirclesContainer = styled.div`
	display: flex;
	gap: 8px;
	place-content: center;
	padding: 8px;
`;

const StepCircle = styled.div<{ active: boolean }>`
	width: 10px;
	height: 10px;
	border: 1px solid ${colors.grey80};
	border-radius: 5px;
	background-color: ${({ active }) => (active ? colors.grey80 : "unset")};
`;

const PageTextContent = styled.div`
	display: grid;
	grid-template-columns: 1fr 2fr;
	padding: 40px;
	padding-bottom: 16px;
	gap: 40px;

	@media ${physioMeMobileBreakpoint} {
		grid-template-columns: 1fr;
		gap: 8px;
		padding: 24px;
	}
`;

const ButtonsContainer = styled.div`
	margin-top: 16px;
	display: flex;
	place-content: center;
	place-items: center;
	gap: 10px;

	@media ${physioMeMobileBreakpoint} {
		& > button {
			width: 100% !important;
		}
	}
`;

const StyledBaseDialog = styled(BaseDialog)`
	.p-dialog-title {
		font-family: ${fonts.body};
		font-weight: 700 !important;
		font-size: 18px !important;
		text-align: center !important;
	}

	.p-dialog-header {
		padding: 16px !important;
	}

	.p-dialog-header::after {
		display: none;
	}

	.p-dialog-content {
		padding: 0;
		scrollbar-color: rgba(1, 1, 1, 0.08) transparent;
		&::-webkit-scrollbar {
			width: 10px;
		}
		&::-webkit-scrollbar-track {
			background: rgba(1, 1, 1, 0.08);
		}
		&::-webkit-scrollbar-thumb {
			background: rgba(1, 1, 1, 0.08);
		}
	}
`;

const AnnouncementImage = styled(ImageWithLoading)`
	object-fit: cover;
	height: 332px;

	@media ${physioMeMobileBreakpoint} {
		height: 282px;
	}
`;

const ImageContainer = styled.div`
	height: 332px;
	width: 100%;
	display: grid;
	justify-content: center;
	align-items: center;
	position: relative;

	@media ${physioMeMobileBreakpoint} {
		height: 282px;
		max-width: 90vw !important;
		margin-left: auto !important;
		margin-right: auto !important;
		border-radius: 18px !important;
		overflow: hidden !important;
	}
`;

const StyledMegaphone = styled(Megaphone)`
	height: 142px;
	transform: rotateZ(5deg);
`;
